import { useQuery } from '@apollo/client';
import { Pivot, PivotItem, Stack, makeStyles } from '@fluentui/react';
import { HistoryActionMenuView } from 'common/components/History';
import {
  MenuPropertiesCount,
  getUpdatedPivotActionItems,
} from 'common/components/Modals/utils';
import { PivotCustomRender } from 'common/components/PivotComponent';
import {
  PivotDisplay,
  PivotMenuItemProps,
} from 'common/components/PivotDisplay';
import { GlobalActions } from 'common/constants';
import {
  ListPaymentNotes,
  ListPaymentNotesVariables,
} from 'common/graphql/__generated__/ListPaymentNotes';
import React, { useCallback, useState } from 'react';
import { PaymentDetailProps } from '..';
import { Files } from './Files';
import { Notes } from './Notes';
import { Tags } from './Tags';
import { pivotActionItemsTags as pivotActionItems } from 'common/components/ActionMenu/PivotActionItems';

import ENTITY_NOTES_LIST from '../../../../../graphql/ListPaymentNotes.graphql';
type ActionMenuProps = Pick<PaymentDetailProps, 'payment'>;

const useStyles = makeStyles(() => ({
  centerAlign: {
    alignItems: 'center',
  },
}));

export const ActionMenu: React.FC<ActionMenuProps> = ({ payment }) => {
  const styles = useStyles();
  const {
    id,
    entityDocumentsByEntityId,
    notesByEntityId,
    paymentHistoriesByEntityId,
    entityTagsByEntityId,
  } = { ...payment };
  let updatedPivotActionItems: PivotMenuItemProps[] = [];
  const [isMenuExpanded, setIsMenuExpanded] = useState<boolean>(true);
  const [selectedKey, setSelectedKey] = useState<string>(GlobalActions.attach);

  const { data: entityNotesData, refetch: entityNotesListRefetch } = useQuery<
    ListPaymentNotes,
    ListPaymentNotesVariables
  >(ENTITY_NOTES_LIST, {
    variables: {
      id: id!,
    },
  });

  const { nodes } = { ...entityNotesData?.payment?.notesByEntityId };

  const getEntityNotes = () => {
    const variables: ListPaymentNotesVariables = {
      id: id!,
    };
    entityNotesListRefetch(variables);
  };

  const getEntityNotesMemo = useCallback(getEntityNotes, [id!]);

  const menuCountArray: MenuPropertiesCount[] = [
    {
      name: 'Files',
      iconName: 'Attach',
      count: entityDocumentsByEntityId?.nodes.length || 0,
    },
    {
      name: 'Notes',
      iconName: 'Page',
      count: notesByEntityId?.nodes?.length || 0,
    },
    {
      name: 'History',
      iconName: 'History',
      count: paymentHistoriesByEntityId?.totalCount || 0,
    },
    {
      name: 'Tags',
      iconName: 'Tag',
      count: entityTagsByEntityId?.nodes?.length || 0,
    },
  ];

  updatedPivotActionItems = payment
    ? getUpdatedPivotActionItems(menuCountArray, pivotActionItems)
    : (updatedPivotActionItems = pivotActionItems);

  const getSelectedSection = (key: string) => {
    switch (key) {
      case GlobalActions.attach:
        return <Files fileListData={entityDocumentsByEntityId!} />;
      case GlobalActions.notes:
        return (
          <Notes
            invoiceId={id!}
            data={nodes || []}
            listRefreshRequest={getEntityNotesMemo}
          />
        );
      case GlobalActions.history:
        return (
          <HistoryActionMenuView
            moduleName="Payment Tracking"
            historyData={paymentHistoriesByEntityId?.nodes || []}
          />
        );
      case GlobalActions.tags:
        return <Tags paymentId={id!} />;
      default:
        return null;
    }
  };

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Stack tokens={{ padding: '10px 20px' }}>
        {isMenuExpanded ? (
          <Stack>
            <Stack horizontalAlign="space-between" horizontal>
              <Stack horizontal className={styles.centerAlign}>
                <Pivot
                  selectedKey={selectedKey}
                  onLinkClick={(item: PivotItem | undefined) => {
                    setSelectedKey(item?.props.itemKey || GlobalActions.attach);
                  }}
                >
                  {updatedPivotActionItems.map((item, index) => {
                    return (
                      <PivotItem
                        key={index}
                        itemKey={item.itemKey}
                        itemIcon={item.iconName}
                        headerText={item.name}
                        itemCount={item.count}
                        onRenderItemLink={PivotCustomRender}
                      />
                    );
                  })}
                </Pivot>
              </Stack>
            </Stack>
            <Stack>{getSelectedSection(selectedKey)}</Stack>
          </Stack>
        ) : (
          <Stack horizontalAlign="space-between" horizontal>
            <Stack horizontal verticalAlign="center">
              <Stack horizontal verticalAlign="center">
                {updatedPivotActionItems.map((item, index) => {
                  return (
                    <PivotDisplay
                      key={index}
                      {...item}
                      onPivotClick={() => {
                        setSelectedKey(item.itemKey!);
                        setIsMenuExpanded(true);
                      }}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
